import { useGetFinancingDataQuery } from '@/services/accounts';
import { useGetQuotesQuery } from '@/services/quotes';
import { Box, Icon } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import {
  AccountDocument,
  AdderType,
  DirectPayMaterialVendorDisplayNameOptions,
  DirectPayProjectStatusLabel,
  DomesticContentEligibilityLabels,
  ProgramType,
  QuoteStatus,
  SafeHarborEquipmentStatusLabels,
  UserPermissions,
} from 'types';

import { useAuth } from '../auth/authProvider';

interface AccountStatusIndicatorsProps {
  account?: AccountDocument;
}

interface BadgeConfig {
  label: string;
  icon?: any;
  iconColor?: any;
  value: string;
  background?: any;
  borderColor?: any;
  fontColor?: any;
  padding?: any;
  style?: React.CSSProperties;
}

export function AccountStatusIndicators({ account }: AccountStatusIndicatorsProps) {
  const { id } = useParams<{ id: any }>();
  const { enableAccountAttributesBadges = false } = useFlags();
  const { claims } = useAuth();
  const isLightReachSupport = Boolean(claims?.permissions.includes(UserPermissions.lightReachSupport));

  const { data: quotes } = useGetQuotesQuery(
    { id, programType: account?.programType },
    { skip: !account?.programType },
  );

  const { data: financingData } = useGetFinancingDataQuery(id ?? '', {
    skip: !enableAccountAttributesBadges || !id || !isLightReachSupport || !account?.firstPaymentDueDate,
  });

  const activeQuote = quotes?.find((quote) => quote.status === QuoteStatus.active);

  const domesticContentBadge: BadgeConfig | null = useMemo(() => {
    const eligibilityStatus = account?.domesticContentEligibilityStatus;

    if (eligibilityStatus) {
      return {
        label: 'Domestic Content:',
        icon: 'check',
        iconColor: 'black',
        value: DomesticContentEligibilityLabels[eligibilityStatus],
        background: 'white',
        borderColor: 'separator',
        fontColor: 'black',
      };
    }

    return null;
  }, [account]);

  const energyCommunityBadge: BadgeConfig | null = useMemo(() => {
    const eligibilityResult = account?.itcAdderQualifications?.ecEligibilityAccountCreation;
    const isEnergyBackup = account?.programType === ProgramType.energyBackup;

    if (!isEnergyBackup || !eligibilityResult) {
      return null;
    }

    return {
      label: 'Energy Community:',
      icon: 'check',
      iconColor: 'black',
      value: eligibilityResult,
      background: 'white',
      borderColor: 'separator',
      fontColor: 'black',
    };
  }, [account?.programType, account?.itcAdderQualifications?.ecEligibilityAccountCreation]);

  const energyStorageBadge: BadgeConfig | null = useMemo(() => {
    const hasBattery = activeQuote?.adders?.some((adder: string) =>
      [AdderType.backupBattery, AdderType.arbitrageBattery].includes(adder as AdderType),
    );

    if (!hasBattery) {
      return null;
    }

    const isBackupBattery = activeQuote?.adders?.includes(AdderType.backupBattery);
    const value = isBackupBattery ? 'Backup Battery' : 'Arbitrage Battery';

    return {
      label: 'Energy Storage:',
      icon: 'check',
      iconColor: 'black',
      value,
      background: 'white',
      borderColor: 'separator',
      fontColor: 'black',
    };
  }, [activeQuote]);

  const directPayBadge: BadgeConfig | null = useMemo(() => {
    const directPayEnabled = account?.directPay?.directPayEnabled;
    const projectStatus = account?.directPay?.projectStatus;
    const directPayMaterialVendorAlias = account?.directPay?.directPayMaterialVendorAlias;
    if (!directPayMaterialVendorAlias || !projectStatus) {
      return null;
    }

    return {
      label: 'Direct Pay:',
      icon: 'check',
      iconColor: 'black',
      value: directPayEnabled
        ? `${DirectPayProjectStatusLabel[projectStatus]} - ${DirectPayMaterialVendorDisplayNameOptions[directPayMaterialVendorAlias]}`
        : DirectPayProjectStatusLabel[projectStatus],
      background: 'white',
      borderColor: 'separator',
      fontColor: 'black',
    };
  }, [account]);

  const hardwareExceptionBadge: BadgeConfig | null = useMemo(() => {
    const equipmentException = account?.equipmentException;

    if (!equipmentException || !equipmentException.equipmentTypes?.length) {
      return null;
    }

    return {
      label: 'Hardware Exception Granted',
      icon: 'tools',
      iconColor: 'warning-500',
      value: '',
      background: 'warning-50',
      borderColor: 'warning-200',
      fontColor: 'warning-700',
    };
  }, [account]);

  const pastDueBadge: BadgeConfig | null = useMemo(() => {
    if (!isLightReachSupport) return null;
    if (!account?.firstPaymentDueDate) return null;
    const amountPastDue = parseFloat(financingData?.amountPastDue ?? '0');
    if (!(amountPastDue > 0)) return null;

    return {
      label: 'Past Due',
      value: '',
      background: 'danger-100',
      borderColor: 'danger-200',
      fontColor: 'danger-700',
      style: { fontWeight: 'bold' },
      padding: 'md',
    };
  }, [isLightReachSupport, account?.firstPaymentDueDate, financingData?.amountPastDue]);

  const safeHarborBadge: BadgeConfig | null = useMemo(() => {
    const status = account?.safeHarborEquipmentStatus;

    if (!status) return null;

    return {
      label: 'Safe Harbor:',
      icon: 'check',
      iconColor: 'black',
      value: SafeHarborEquipmentStatusLabels[status],
      background: 'white',
      borderColor: 'separator',
      fontColor: 'black',
    };
  }, [account]);

  if (!enableAccountAttributesBadges) {
    return null;
  }

  const badges = [
    domesticContentBadge,
    energyCommunityBadge,
    energyStorageBadge,
    directPayBadge,
    hardwareExceptionBadge,
    safeHarborBadge,
    pastDueBadge,
  ].filter(Boolean) as BadgeConfig[];

  if (!badges.length) {
    return null;
  }

  return (
    <Box direction="row" childGap="md">
      {badges.map((badge, index) => (
        <Box
          key={index}
          borderWidth="xs"
          borderColor={badge.borderColor || 'separator'}
          radius="md"
          direction="row"
          alignItems="center"
          fontSize="sm"
          overflow="hidden"
          background={badge.background || 'white'}
          childGap="xs"
          width="auto"
          height="sm"
          padding={badge.padding ?? 'md sm'}
        >
          {badge.icon && <Icon name={badge.icon} size="md" color={badge.iconColor} />}
          <Box fontWeight="medium" color={badge.fontColor} style={badge.style}>
            {badge.label}
          </Box>
          {badge.value && (
            <Box fontWeight="bold" color={badge.fontColor}>
              {badge.value}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}
